// Original teaching examples. Reading links are topic references, not reproduced articles.
export const foundationLessons = [
  {
    id: 'cash-flow', moduleId: 'foundation',
    title: '先知道錢去哪裡', subtitle: '收支與現金流', minutes: 5, tag: '打好基礎',
    objectives: ['分清楚收入、支出與帳戶轉帳', '把不定期帳單放進每月預算'],
    paragraphs: [
      '現金流記錄一段期間內實際進來與出去的錢。薪資入帳、付房租是現金流；股票帳面上漲而未出售，則沒有因此多收到現金。先選定月份，再把必要支出、可調整支出與還款整理出來。',
      '每年才繳一次的保費或訂閱，容易讓普通月份看起來很寬裕。做預算時可把年度金額除以 12，逐月預留；記帳時仍保留實際付款日期，兩者用途不同。',
      '從自己的銀行帳戶領錢到錢包，只是錢換位置，不是新的收入或生活支出。信用卡消費與後續繳卡費，也要選定一致的記錄方法，避免同一筆購物被算兩次。',
    ],
    example: {
      title: '假設：這個月結餘 12,000 元，都能投入嗎？',
      steps: ['收入 44,000 元，生活支出與還款 32,000 元，帳面結餘為 12,000 元。', '一年固定帳單合計 24,000 元，每月先預留 2,000 元。', '可再分配金額為 10,000 元，再依預備金與其他目標安排。'],
      conclusion: '結餘是一個起點；預算還要替尚未到期的生活需要保留空間。',
    },
    pitfalls: ['把自己帳戶間的轉帳當收入，會高估賺到的錢。', '只看單月消費，可能漏掉年繳帳單或季節性支出。'],
    glossary: [{ term: '現金流', definition: '指定期間內的現金流入與流出。' }, { term: '預算', definition: '事先分配未來資金用途的計畫，與事後記帳互相配合。' }],
    takeaway: '投資的起點，是知道每月能穩定留下多少錢。',
    exercise: '在 My Ledger 查最近一個月收支，挑出三項最大支出；再列一筆年繳費用，換算每月應預留的金額。',
    quiz: {
      question: '月收入 40,000 元，支出及還款共 30,000 元，另為年度費用預留 2,000 元，可再分配多少？',
      options: ['8,000 元', '10,000 元', '12,000 元'], answer: 0,
      explanation: '40,000 − 30,000 − 2,000 = 8,000 元。這筆餘額還要依預備金與其他目標分配，並非全都適合投資。',
    },
    sources: [
      { label: '市場先生｜記帳常見迷思', url: 'https://rich01.com/blog-pos-13/', kind: 'reading' },
      { label: 'SEC Investor.gov｜預算、儲蓄與投資', url: 'https://www.investor.gov/build-wealth-over-time-through-saving-and-investing', kind: 'official' },
    ],
  },
  {
    id: 'emergency-fund', moduleId: 'foundation',
    title: '先準備生活緩衝', subtitle: '緊急預備金', minutes: 5, tag: '照顧生活',
    objectives: ['區分突發需要與已知支出', '依生活責任設定預備金，而非套用固定月數'],
    paragraphs: [
      '緊急預備金用來應付收入中斷、突發修繕等未預期狀況。已知的房租、年繳保費與旅遊計畫應另外編預算，才不會每次付帳都消耗緊急資金。',
      '可先用「每月必要支出 × 自訂月數」估算，再考慮收入穩定度、家庭責任與過往突發支出。月數沒有人人適用的答案；先建立小額緩衝，再逐步增加，也能改善臨時借款的壓力。',
      '這筆錢需要能適時動用，且價值相對穩定。股票與長天期債券基金可能在需要用錢時下跌；信用卡額度則是未來需要償還的借款，不能直接當作已有的預備金。',
    ],
    example: {
      title: '假設：把目標拆成能執行的步驟',
      steps: ['必要生活費每月 22,000 元，自行設定準備 5 個月：目標 110,000 元。', '已存 38,000 元，尚差 72,000 元。', '若每月可存 6,000 元、不計利息，需要 12 個月；期間動用時再更新缺口。'],
      conclusion: '目標、現有金額與每月可存金額要一起看，預備金也需要補回。',
    },
    pitfalls: ['把明年確定要繳的學費算成緊急預備金，會重複分配同一筆錢。', '因為想提高報酬，把全部緩衝金放在價格波動大的商品。'],
    glossary: [{ term: '流動性', definition: '資產能多快、以多接近合理價格換成可使用的現金。' }, { term: '必要支出', definition: '維持基本生活與履行必要付款責任的支出。' }],
    takeaway: '預備金的用途是保留生活選擇，不是追求最高收益。',
    exercise: '列出必要支出與一項曾遇過的突發費用，再到 Tools 試算目標和缺口，寫下你選擇準備月數的理由。',
    quiz: {
      question: '每月必要支出 25,000 元，假設自行設定準備 4 個月，需要多少？',
      options: ['100,000 元', '25,000 元', '75,000 元'], answer: 0,
      explanation: '25,000 × 4 = 100,000 元。這是練習假設，不代表 4 個月一定適合你或保證足夠。',
    },
    sources: [
      { label: '市場先生｜緊急預備金', url: 'https://rich01.com/emergency-reserve/', kind: 'reading' },
      { label: 'CFPB｜建立緊急預備金', url: 'https://www.consumerfinance.gov/an-essential-guide-to-building-an-emergency-fund/', kind: 'official' },
    ],
  },
  {
    id: 'compound-inflation', moduleId: 'numbers',
    title: '看懂錢怎麼變大、變小', subtitle: '複利與通膨', minutes: 6, tag: '理解數字',
    objectives: ['理解收益留在本金後的累積效果', '區分帳面成長與購買力'],
    paragraphs: [
      '複利是收益留在本金中，下一期一起計算。以固定利率的簡化模型來看，本利和 = 本金 ×（1 + 每期利率）的期數次方。利率與期數的單位要一致：年利率搭配年數，月利率搭配月數。',
      '投資的年度報酬通常不固定；每年的本金會被上一年的漲跌改變。因此先漲 10% 再跌 10%，不會回到原點，負報酬也會影響後續累積。試算的平滑曲線只是情境，不是市場承諾。',
      '通膨是整體物價上升，會改變錢的購買力。扣除通膨的實質報酬可用（1 + 名目報酬）÷（1 + 通膨率）− 1 計算；消費者物價指數反映整體消費組合，個人的開銷變化可能不同。',
    ],
    example: {
      title: '假設：存下的錢變多，能買的東西也變多嗎？',
      steps: ['20,000 元以每年固定 4% 複利，兩年後為 20,000 × 1.04² = 21,632 元。', '若同期物價每年上升 2%，以起始年度的購買力換算為 21,632 ÷ 1.02²，約 20,792 元。', '第一年的實質報酬為 1.04 ÷ 1.02 − 1，約 1.96%；例子未計費用及稅負。'],
      conclusion: '金額增加 8.16%，不等於購買力也增加 8.16%。',
    },
    pitfalls: ['把兩年的百分比直接相加，忽略每年計算基礎改變。', '把過去平均報酬填入試算後，當成每年都會實現的收益。'],
    glossary: [{ term: '名目報酬', definition: '尚未扣除物價變化影響的報酬。' }, { term: '實質報酬', definition: '調整通膨後，反映購買力變化的報酬。' }],
    takeaway: '看累積成果，也看扣除成本與物價變化後的購買力。',
    exercise: '在 Tools 比較 0%、正報酬與負報酬情境，再手算「先漲 20%、後跌 20%」後還剩多少本金比例。',
    quiz: {
      question: '10,000 元第一年增加 10%，第二年減少 10%，最後是多少？',
      options: ['9,900 元', '10,000 元', '10,100 元'], answer: 0,
      explanation: '10,000 × 1.1 × 0.9 = 9,900 元。漲跌的計算基礎不同，相同百分比不會互相抵消。',
    },
    sources: [
      { label: '市場先生｜單利與複利', url: 'https://rich01.com/what-is-compound-interest/', kind: 'reading' },
      { label: 'SEC Investor.gov｜複利試算', url: 'https://www.investor.gov/financial-tools-calculators/calculators/compound-interest-calculator', kind: 'official' },
      { label: '主計總處｜物價與購買力換算', url: 'https://www.stat.gov.tw/cpi2.aspx?n=4583', kind: 'official' },
    ],
  },
  {
    id: 'investment-tools', moduleId: 'funds',
    title: '認識投資工具', subtitle: '股票、債券、基金與 ETF', minutes: 6, tag: '認識商品',
    objectives: ['分辨持有公司、借錢與集合投資', '從資產內容判斷商品特性'],
    paragraphs: [
      '持有股票，是參與公司的所有權，收益與損失取決於公司營運、市場評價等因素。持有債券，則是對發行人的債權，約定支付不等於一定收得到，仍有信用風險。',
      '基金是集合資金進行投資的安排；ETF 則能在交易所買賣。ETF 可以持有股票、債券或其他資產，也分追蹤指數的被動式與由經理人主動決策的類型。名稱裡有 ETF，不代表只買股票，也不代表保本。',
      '先問「它持有什麼、如何決策、靠什麼產生報酬」，再看費用與風險。兩檔名稱不同的 ETF 可能持有相似公司；同樣叫債券基金，也可能因存續期間與信用品質不同而有很不一樣的波動。',
    ],
    example: {
      title: '假設：同樣投資一家飲料公司，權利可能不同',
      steps: ['買它的股票：成為股東，股價與股利都可能變動。', '買它發行的債券：成為債權人，依契約收款，但仍可能違約。', '買一檔持有多家公司的 ETF：透過基金間接持有資產，結果由整體內容與費用共同影響。'],
      conclusion: '同一家公司可以出現在不同工具中，但承擔的權利與風險並不相同。',
    },
    pitfalls: ['把 ETF 當作固定收益商品，忽略持有資產的價格變動。', '只看商品名稱與配息率，沒有閱讀持有內容或投資策略。'],
    glossary: [{ term: '股權', definition: '對公司的所有權權益。' }, { term: '債權', definition: '依契約向債務人請求償付的權利。' }, { term: '公開說明書', definition: '說明商品策略、費用、風險與相關條件的文件。' }],
    takeaway: '先讀懂商品持有什麼、如何運作，再決定是否適合。',
    exercise: '找一檔 ETF 的公開資料，只記下投資資產、主動或被動、費用與兩項風險，暫時不比較近期漲幅。',
    quiz: {
      question: '關於 ETF，哪個說法正確？',
      options: ['ETF 是基金的一種，也可能虧損', '所有 ETF 都保本', '所有 ETF 都只買股票'], answer: 0,
      explanation: 'ETF 的風險取決於持有資產、策略與交易條件；名稱有 ETF，不代表保本或一定分散。',
    },
    sources: [
      { label: '市場先生｜ETF 入門', url: 'https://rich01.com/etf0050/', kind: 'reading' },
      { label: '證交所｜ETF 種類與風險', url: 'https://www.twse.com.tw/zh/products/securities/etf/overview/introduction.html', kind: 'official' },
      { label: 'SEC Investor.gov｜股票、債券與風險', url: 'https://www.investor.gov/introduction-investing/investing-basics/what-risk', kind: 'official' },
    ],
  },
  {
    id: 'diversification', moduleId: 'strategy',
    title: '建立可持續的習慣', subtitle: '分散與定期定額', minutes: 6, tag: '建立方法',
    objectives: ['辨認表面分散與實際持有重疊', '算出固定投入的單位數與平均成本'],
    paragraphs: [
      '分散投資是降低對單一公司、產業或市場的依賴。持有很多商品名稱，不代表風險來源不同；如果多檔基金的大部位都在同樣幾家公司，一個產業衝擊仍可能一起影響它們。分散也無法消除整個市場下跌的風險。',
      '定期定額是在固定時點投入固定金額。價格低時同額能買較多單位，價格高時較少，重點是規律與資金安排，不是保證最低成本或獲利。投資內容失去合理性時，持續扣款也不會自動解決問題。',
      '評估單筆投入與分批投入時，要先分清楚是「每月才領到收入」，還是「已有整筆錢，選擇慢慢投入」。資金真正參與市場的時間不同，不能只拿最後賺多少直接比較方法好壞。',
    ],
    example: {
      title: '假設：固定金額，不是固定股數',
      steps: ['兩次各投入 1,200 元，單位價格分別為 60 元與 40 元。', '分別買到 20 與 30 單位，總成本 2,400 元、共 50 單位，平均每單位 48 元。', '若之後價格為 36 元，總值只剩 1,800 元，仍虧損 600 元；全部忽略費用。'],
      conclusion: '平均成本不是兩個價格的簡單平均，規律投入也不會消除價格下跌。',
    },
    pitfalls: ['看見三檔 ETF 就認為已充分分散，未檢查共同持股。', '把平均成本降低誤認成已經獲利。'],
    glossary: [{ term: '持股重疊', definition: '不同商品持有相同公司，讓實際風險比表面上更集中。' }, { term: '平均成本', definition: '累計買進成本除以累計持有單位數。' }],
    takeaway: '分散與規律有助管理風險，但都不是獲利保證。',
    exercise: '寫下每月可投入的餘裕與扣款日期，再挑兩檔基金比較前幾大持股；這次練習不需要下單。',
    quiz: {
      question: '每次投入 1,000 元，第一次價格 100 元、第二次 50 元，忽略費用，合計買到多少單位？',
      options: ['30 單位', '20 單位', '40 單位'], answer: 0,
      explanation: '第一次 10 單位、第二次 20 單位，平均成本約 66.67 元。之後價格若繼續跌，仍會虧損。',
    },
    sources: [
      { label: '市場先生｜定期定額的運作', url: 'https://rich01.com/dollar-cost-averaging/', kind: 'reading' },
      { label: '證交所｜定期定額投資', url: 'https://www.twse.com.tw/zh/products/system/ftfa-trading.html', kind: 'official' },
      { label: 'SEC Investor.gov｜分散投資入門', url: 'https://www.investor.gov/additional-resources/general-resources/publications-research/info-sheets/beginners-guide-asset', kind: 'official' },
    ],
  },
  {
    id: 'costs-and-fraud', moduleId: 'practice',
    title: '下單前先確認', subtitle: '費用、風險與防詐', minutes: 5, tag: '守住資產',
    objectives: ['把交易與持有成本放進決策', '辨識需要停止付款並查證的情境'],
    paragraphs: [
      '商品可能收取買賣手續費、持有期間費用或其他成本。基金費用有些直接從基金資產扣除，不會每次出現在你的扣款通知裡；比較績效時，要確認數字已扣哪些成本。',
      '「保證高報酬」、催你立刻轉帳、要求提供密碼與驗證碼，都是需要停下來查證的訊號。名稱、Logo 與獲利畫面可以被仿冒，不能只靠聊天群組傳來的連結判斷真偽。',
      '如果對方說必須再繳保證金或稅金才能出金，先停止新增付款，保存對話與交易紀錄，再透過自行找到的官方管道查證。曾成功小額提領，也不等於平台可靠。',
    ],
    example: {
      title: '假設：只看獲利畫面，會漏掉什麼？',
      steps: ['投入 15,000 元，帳面收益顯示 900 元。', '如果這 900 元尚未扣除合計 180 元的成本，實際淨收益只剩 720 元。', '再確認資產與交易紀錄是否能從合法機構的官方管道核對，畫面數字本身不是證明。'],
      conclusion: '計算成本與確認交易真實性，是兩件都要做的事。',
    },
    pitfalls: ['把沒有另外收帳單的基金費用當成零成本。', '為了領回原本的錢，因對方催促而持續加碼付款。'],
    glossary: [{ term: '交易成本', definition: '買進或賣出時產生的費用與其他交易摩擦。' }, { term: '持有成本', definition: '持有商品期間持續發生、可能已反映於淨值的費用。' }],
    takeaway: '不懂先查證，不因催促、獲利截圖或保證話術匯款。',
    exercise: '替一個商品列出買進、持有、賣出成本；再練習從自行查到的官方網站確認交易機構身分。',
    quiz: {
      question: '群組老師說「先繳保證金，才能領回獲利」，應如何處理？',
      options: ['停止匯款，保存紀錄並透過官方管道查證', '立刻匯款，避免錯過領款期限', '借錢補足保證金'], answer: 0,
      explanation: '用保證金、稅金等理由要求持續付款，是假投資常見手法；不要為了領回原本的錢再投入更多。',
    },
    sources: [
      { label: '市場先生｜高報酬與低風險的迷思', url: 'https://rich01.com/3-5/', kind: 'reading' },
      { label: '證交所｜證券投資反詐騙', url: 'https://www.twse.com.tw/anti-fraud/zh/', kind: 'official' },
      { label: 'SEC Investor.gov｜費用如何影響投資', url: 'https://www.investor.gov/introduction-investing/general-resources/news-alerts/alerts-bulletins/investor-bulletins/updated', kind: 'official' },
    ],
  },
  {
    id: 'financial-goals', moduleId: 'foundation',
    title: '把願望寫成有期限的目標', subtitle: '金額、期限與優先順序', minutes: 5, tag: '開始規劃',
    objectives: ['把模糊願望改成可計算的目標', '分辨目標缺口與能承擔的風險'],
    paragraphs: [
      '「我想變有錢」很難直接安排下一步。改成「兩年後要準備多少錢、已經有多少、每月能存多少」，就能檢查計畫是否可行。每個目標都要寫用途與期限，並分清楚必要與可延後的項目。',
      '期限會影響資金能承受多少不確定性。下半年就要繳的學費，即使你心理上願意冒險，也可能沒有時間等待價格回升；風險意願與生活實際承擔能力是不同的事。',
      '如果試算發現缺口，能調整的不只有報酬率：也可以降低目標金額、延後時間或提高可儲蓄金額。直接假設超高報酬，會讓紙上計畫看似成立，卻沒有解決缺口。',
    ],
    example: {
      title: '假設：18 個月後的進修計畫',
      steps: ['課程與生活預備預計需 120,000 元，已有專款 30,000 元，缺口 90,000 元。', '忽略利息，18 個月內補足缺口，需要每月存 5,000 元。', '若實際每月只能存 3,000 元，18 個月後總額為 84,000 元，還差 36,000 元，需調整計畫。'],
      conclusion: '先讓收入、目標與期限對得上，再考慮適合的資金安排。',
    },
    pitfalls: ['把同一筆存款同時分配給旅遊、預備金與投資。', '為了讓試算達標，任意提高假設報酬而未處理風險。'],
    glossary: [{ term: '投資期限', definition: '距離這筆資金預計使用時點的時間。' }, { term: '目標缺口', definition: '目標金額扣除已專門保留的可用資金。' }],
    takeaway: '先決定錢要做什麼，再決定讓它承擔多少不確定性。',
    exercise: '寫一個一年內與一個五年以上的財務目標，各填用途、金額、期限、現有專款與每月可存金額。',
    quiz: {
      question: '目標金額 90,000 元，已存 18,000 元，還有 12 個月，忽略利息每月需存多少？',
      options: ['7,500 元', '6,000 元', '1,500 元'], answer: 1,
      explanation: '缺口是 90,000 − 18,000 = 72,000 元，再除以 12 個月，得到每月 6,000 元。',
    },
    sources: [
      { label: '市場先生｜釐清自己學投資的目的', url: 'https://rich01.com/professional-investors/', kind: 'reading' },
      { label: 'SEC Investor.gov｜設定財務目標', url: 'https://www.investor.gov/introduction-investing/investing-basics/save-and-invest/define-your-goals', kind: 'official' },
    ],
  },
  {
    id: 'balance-sheet', moduleId: 'foundation',
    title: '你擁有多少，也要看欠多少', subtitle: '資產、負債與淨資產', minutes: 5, tag: '盤點生活',
    objectives: ['建立同一日期的個人資產負債表', '理解淨資產與可立即動用資金的差別'],
    paragraphs: [
      '資產負債表像一張指定日期的照片：資產是當下擁有的價值，負債是尚未清償的金額，淨資產 = 資產 − 負債。收入很高與淨資產很高，不是同一回事。',
      '盤點時，存款用帳面餘額，上市投資可用同日市值，貸款用未償還餘額。不要把房子市值計入資產後漏掉房貸；也不要把信用卡已消費但尚未繳清的金額忘記。',
      '淨資產不等於今天可花的現金。房屋或其他難出售資產可能有價值，卻無法立即拿來繳帳單；因此可另外標記哪些資產容易動用、哪些已有特定用途。',
    ],
    example: {
      title: '假設：帳戶裡的錢，有多少真正屬於自己？',
      steps: ['同一天的存款 160,000 元，投資市值 90,000 元，資產合計 250,000 元。', '未清償貸款 70,000 元、信用卡款 12,000 元，負債合計 82,000 元。', '淨資產 = 250,000 − 82,000 = 168,000 元；其中投資市值仍會波動。'],
      conclusion: '同時記錄資產與負債，才能看見完整的財務位置。',
    },
    pitfalls: ['把信用卡可用額度或尚未獲得的薪資當成已有資產。', '混用不同日期的市值與貸款餘額，造成不一致的比較。'],
    glossary: [{ term: '淨資產', definition: '資產總值扣除負債總額後的餘額，也稱淨值。' }, { term: '未償還餘額', definition: '某一時點仍需要償付的債務金額。' }],
    takeaway: '淨資產看財務位置，現金流看一段期間的變化，兩張表要一起讀。',
    exercise: '以本月底為同一基準日，列出三項資產與所有未清償負債，計算淨資產，並圈出近期能動用的部分。',
    quiz: {
      question: '資產 420,000 元、負債 150,000 元，淨資產是多少？',
      options: ['420,000 元', '570,000 元', '270,000 元'], answer: 2,
      explanation: '420,000 − 150,000 = 270,000 元。負債不能因為未到期就忽略，也不能加進資產。',
    },
    sources: [
      { label: '市場先生｜自住房與資產盤點', url: 'https://rich01.com/home-in-asset-allocation/', kind: 'reading' },
      { label: 'SEC Investor.gov｜盤點財務與淨資產', url: 'https://www.investor.gov/introduction-investing/investing-basics/save-and-invest/figure-out-your-finances', kind: 'official' },
    ],
  },
  {
    id: 'total-return', moduleId: 'numbers',
    title: '領到配息，真的就是賺錢嗎？', subtitle: '價差、配息與總報酬', minutes: 6, tag: '讀懂績效',
    objectives: ['把價格變動與現金收入合併計算', '辨識配息率與投資報酬率的差異'],
    paragraphs: [
      '只看領到多少股息，會漏掉資產價值變化。對沒有中途加碼、沒有把配息再投入的簡單持有案例，總損益可用「期末市值 + 累計現金配息 − 原始投入 − 尚未計入的費用」計算，報酬率再除以原始投入。這裡的原始投入包含買進時已付成本，同一項費用只計一次。',
      '配息讓一部分資產價值轉成現金，不是憑空多出一份財富。除息參考價或基金淨值會反映分配，後續市價仍由交易決定。因此即使一直收到配息，總報酬仍可能是負數。',
      '比較績效前，先對齊日期、幣別、費用與配息再投入假設。如果使用的是已含配息再投入的總報酬資料，就不能再把配息加一次；有中途加碼或提領，也需要能處理現金流的績效方法。',
    ],
    example: {
      title: '假設：有現金收入，仍可能虧損',
      steps: ['投入 12,000 元，期間收到 720 元配息，沒有再投入。', '期末持有市值為 10,800 元，忽略費用及稅負。', '總損益 = 10,800 + 720 − 12,000 = −480 元，總報酬率 = −480 ÷ 12,000 = −4%。'],
      conclusion: '6% 的現金配息比例，不能單獨代表這筆投資賺了 6%。',
    },
    pitfalls: ['把「含息報酬」再加配息，重複計算同一項收益。', '只比較配息金額，沒有看淨值、市價或投入成本。'],
    glossary: [{ term: '價差損益', definition: '資產售價或目前市值相對買進成本的變化。' }, { term: '總報酬', definition: '合併資產價值變動與投資收入後的報酬，仍須確認費用和計算假設。' }],
    takeaway: '配息回答收到多少現金；總報酬回答整體投資增加或減少多少。',
    exercise: '用假想投入 20,000 元、配息 1,000 元，分別代入期末市值 19,000 與 22,000 元，計算兩種總報酬。',
    quiz: {
      question: '投入 10,000 元，配息 500 元未再投入，期末市值 9,200 元，忽略成本，總報酬率為何？',
      options: ['+5%', '−3%', '−8%'], answer: 1,
      explanation: '（9,200 + 500 − 10,000）÷ 10,000 = −3%。配息補回部分價格損失，但整體仍虧損。',
    },
    sources: [
      { label: '市場先生｜高股息與總報酬的差別', url: 'https://rich01.com/high-dividend-etfs-qa1/', kind: 'reading' },
      { label: 'SEC Investor.gov｜檢查績效計算假設', url: 'https://www.investor.gov/introduction-investing/general-resources/news-alerts/alerts-bulletins/investor-bulletins-47', kind: 'official' },
    ],
  },
  {
    id: 'annualized-return', moduleId: 'numbers',
    title: '把不同時間的報酬放在同一把尺上', subtitle: '年化報酬與比較條件', minutes: 6, tag: '比較數字',
    objectives: ['區分累計報酬與複合年化報酬', '知道何時不能直接使用期初期末公式'],
    paragraphs: [
      '一年賺 20% 與十年合計賺 20%，使用的時間不同。複合年化報酬把整段成果換成一個等效的固定年增率，讓時間尺度一致，但不代表真實每一年都得到同樣報酬。',
      '在沒有中途投入或提領、收益已保留於期末價值的簡化條件下，年化報酬 =（期末價值 ÷ 期初價值）的（1 ÷ 年數）次方 − 1。直接將總報酬除以年數，忽略了複合成長。',
      '中途加碼、領息或提領會改變現金流，不能把帳戶變大都當投資成果。這時可進一步學習時間加權報酬與金額加權報酬。即使換成年化，仍需一起比較風險、費用與涵蓋期間，短期年化也不是未來預測。',
    ],
    example: {
      title: '假設：兩年增加 21%，等於每年 10.5% 嗎？',
      steps: ['一次投入 50,000 元，沒有其他現金流，兩年後價值 60,500 元。', '累計報酬為 60,500 ÷ 50,000 − 1 = 21%。', '複合年化為 √1.21 − 1 = 10%；用 50,000 × 1.1 × 1.1 可驗算回 60,500 元。'],
      conclusion: '10% 是等效年增率，並不說明第一年或第二年實際漲跌。',
    },
    pitfalls: ['帳戶因為加碼變大，卻把增加部分全部算成報酬。', '把一週獲利換算成年化後，認為全年都能複製同樣成果。'],
    glossary: [{ term: '累計報酬率', definition: '一整段持有期間的報酬率，必須同時說明期間。' }, { term: '複合年化報酬率', definition: '將多期成果換算成等效的固定年度成長率。' }],
    takeaway: '年化讓時間可比較；它不能消除風險差異，也不能保證未來。',
    exercise: '假設沒有中途現金流，兩年從 40,000 元變 48,400 元，分別算累計與年化報酬，再寫下公式的適用條件。',
    quiz: {
      question: '沒有中途現金流，100 元兩年後變 144 元，複合年化報酬是多少？',
      options: ['22%', '44%', '20%'], answer: 2,
      explanation: '1.2 × 1.2 = 1.44，所以年化報酬是 20%。44% 是兩年累計報酬，除以 2 並非複合年化。',
    },
    sources: [
      { label: '市場先生｜報酬與年化的計算', url: 'https://rich01.com/return-of-investment/', kind: 'reading' },
      { label: 'FINRA｜理解投資報酬', url: 'https://syndication.finra.org/content/how-are-your-investments-doing-returns-explained', kind: 'official' },
    ],
  },
  {
    id: 'risk-drawdown', moduleId: 'numbers',
    title: '投資跌下來，離回本還有多遠？', subtitle: '波動、回撤與損失', minutes: 6, tag: '理解風險',
    objectives: ['從歷史高點計算回撤', '分辨波動、實際損失與承受能力'],
    paragraphs: [
      '風險不只有每天漲跌。公司違約、急用錢卻賣不掉、匯率變動，以及本金永久受損，都可能影響結果。價格波動容易觀察，但不能代表全部風險。',
      '回撤描述價值相對先前高點的下跌程度：回撤幅度 =（先前高點 − 目前價值）÷ 先前高點。最大回撤是觀察期間內，從某個高點到之後低點最大的跌幅；資料期間不同，數字也會不同。',
      '跌後回本所需的漲幅，以跌後價值為基準。損失越大，回到原點需要的漲幅越高。歷史最大回撤只是過去紀錄，未來仍可能出現更大損失；長期持有也不保證單一公司能復原。',
    ],
    example: {
      title: '假設：高點 80,000 元，跌到 60,000 元',
      steps: ['回撤幅度 =（80,000 − 60,000）÷ 80,000 = 25%。', '要回到 80,000 元，需從 60,000 元增加 20,000 元，漲幅約 33.33%。', '假如高點後曾先跌到 56,000 元再回升到 60,000 元，這段紀錄的最大回撤是 30%，大於目前回撤。'],
      conclusion: '回本所需漲幅與原來跌幅不同，過程最深的跌幅也不同於今天的狀態。',
    },
    pitfalls: ['以為跌 50% 之後漲 50% 就能回本；其實需要漲 100%。', '把回測中的最大跌幅當成未來損失上限。'],
    glossary: [{ term: '波動', definition: '價格或報酬在不同時間變動的程度。' }, { term: '最大回撤', definition: '指定歷史期間中，由先前高點到其後低點的最大跌幅。' }],
    takeaway: '問能賺多少之前，也要問跌多少會讓生活或計畫無法承受。',
    exercise: '用假想 100,000 元分別下跌 10%、30%、50%，算剩餘金額與回本所需漲幅；再思考哪種損失會影響近期支出。',
    quiz: {
      question: '一筆沒有其他現金流的投資從 200 元跌到 160 元，要回到 200 元需要漲多少？',
      options: ['20%', '25%', '40%'], answer: 1,
      explanation: '需要增加 40 元，以跌後 160 元為基準：40 ÷ 160 = 25%。原本跌幅則是 40 ÷ 200 = 20%。',
    },
    sources: [
      { label: '市場先生｜回測與最大回撤', url: 'https://rich01.com/what-is-backtesting/', kind: 'reading' },
      { label: 'SEC Investor.gov｜投資風險的種類', url: 'https://www.investor.gov/introduction-investing/investing-basics/what-risk', kind: 'official' },
    ],
  },
];
